import AppModal from "components/Modal";
import { loginKeychain } from "helpers/keychain";
import { loginWithWif } from "helpers/steem";
import { backend_login, backend_get_challenge } from "services/db-service";
import * as dsteem from 'dsteem';
import React, { useState, useEffect }  from 'react';
import ReactDOM from "react-dom";
import { useForm } from "react-hook-form";
import { useAuth } from '../../../helpers/authContext';
import { useToast } from "components/ui/use-toast";

import {
  Button,
  Card,
  CardBody,
  FormGroup,
  Form, 
  Label,
  Alert
} from "reactstrap";
import { packSessionData } from "helpers/localStorage";

type FormData = {
  username: string, 
  wif?: string
};

type Props = {
  isShowing: boolean,
  hide: any, 
  requiredActive?:boolean,
  onSuccess?: (data: {
    username: string;
    wif?: string;
  }) => void;
  onSignup?: () => void;
}

export const LoginModal = ({ isShowing, hide, requiredActive = false, onSuccess, onSignup }: Props) => {
  const { register, handleSubmit, setValue, watch } = useForm<FormData>(); 
  const watchedUsername = watch("username");
  const { login, user, savedAccounts, switchAccount, removeSavedAccount } = useAuth();
  const { toast } = useToast();
  const [errorMessage, setErrorMessage] = useState<string | null>(null); 
  const [focusUser, setFocusUser] = useState(false);
  const [focusPass, setFocusPass] = useState(false);
  const [remindAccount, setRemindAccount] = useState(true);

  useEffect(() => {
    if (requiredActive && user) {
      setValue("username", user);
    }
  }, [requiredActive, user, setValue]);

  const avatarUrl = watchedUsername 
    ? `https://steemitimages.com/u/${watchedUsername.toLowerCase().replace("@","").trim()}/avatar`
    : `https://steemitimages.com/u/steem/avatar`;

  const onSubmit = async (data: FormData) => {
    let username = data.username.toLowerCase().replace("@","").trim();
    let wif = data.wif;

    if (onSuccess) {
      onSuccess({ username, wif });
      hide();
      return;
    }

    let challenge: string;
    try {
      challenge = await backend_get_challenge(username);
    } catch {
      setErrorMessage("Unable to request a login challenge. Please retry.");
      return;
    }

    if (!wif) 
    { 
      loginKeychain(username, challenge)
        .then(async (response: any) => {
          if (response.success) {
            const auth = await backend_login(username, response.result, challenge);
            if (auth.success) {
              login({ username, token: auth.token }, null, remindAccount, 'keychain'); 
              hide();
            } else {
              setErrorMessage("Backend auth failed: " + auth.error);
            }
          } else {
            setErrorMessage(response.message || "Signature required for security.");
          }
        });
    } else { 
      loginWithWif(username, wif)
        .then(async (result: any) => {
          if (result.success) {
            try {
              // Use dsteem for a signature compatible with the backend
              const privateKey = dsteem.PrivateKey.fromString(result.wif);
              const signature = privateKey.sign(dsteem.cryptoUtils.sha256(challenge)).toString();
              
              const auth = await backend_login(username, signature, challenge);
              
              if (auth.success) {
                let autologin = packSessionData({ user: username, password: result.wif! });
                login({ username, token: auth.token }, autologin, remindAccount, 'wif');
                hide();
              } else {
                setErrorMessage("Backend auth failed: " + auth.error);
              }
            } catch (err) {
              setErrorMessage("Error signing with WIF.");
            }
          } else {
            setErrorMessage(result.message);
          }
        })
        .catch(error => {
          console.error("WIF login error:", error);
          setErrorMessage("Something went wrong with WIF.");
        });
    }
  };  

  const fileRef = React.useRef<HTMLInputElement>(null);

  const handleFileRead = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      const userMatch = content.match(/Username:?\s*([a-z0-9.-]+)/i);
      const postingMatch = content.match(/Posting(?: Private Key| Key)?:?\s*([5JK][a-zA-Z0-9]{45,55})/i);
      const activeMatch = content.match(/Active(?: Private Key| Key)?:?\s*([5JK][a-zA-Z0-9]{45,55})/i);
      
      if (userMatch && userMatch[1]) {
        setValue("username", userMatch[1]);
      }
      
      let keyFound = false;
      if (requiredActive) {
        if (activeMatch) {
          setValue("wif", activeMatch[1]);
          keyFound = true;
        }
      } else {
        if (postingMatch) {
          setValue("wif", postingMatch[1]);
          keyFound = true;
        }
      }

      if (!keyFound) {
        toast({
          title: "Incompatible File",
          description: "Could not find valid keys in this file.",
          variant: "rose_red"
        });
      } else if (!userMatch) {
        toast({
          title: "Keys Added",
          description: "Keys extracted successfully. Please type your username manually to login.",
        });
      }
      
      if (fileRef.current) fileRef.current.value = "";
    };
    reader.readAsText(file);
  };

  const renderForm = (
    <div className="modal-body p-0">
      <Card className="shadow border-0">
        <CardBody className="px-lg-5 py-lg-5 body-gray">
          {errorMessage && <Alert color="danger">{errorMessage}</Alert>}

          {/* Clean Saved Accounts Quick-Switch Section */}
          {savedAccounts && savedAccounts.length > 0 && !requiredActive && (
            <div className="mb-4 text-center">
              <div className="d-flex flex-wrap justify-content-center gap-2">
                {savedAccounts.map((acc) => {
                  const isCurrent = user && acc.username.toLowerCase() === user.toLowerCase();
                  return (
                    <div
                      key={acc.username}
                      className="d-inline-flex align-items-center rounded-pill px-2 py-1 mb-1"
                      style={{
                        cursor: isCurrent ? 'default' : 'pointer',
                        backgroundColor: 'var(--speem-gray)',
                        border: isCurrent ? '1px solid var(--speem)' : '1px solid var(--borders-light-clear)',
                        transition: 'all 0.2s',
                      }}
                      onClick={async () => {
                        if (!isCurrent) {
                          await switchAccount(acc.username);
                          hide();
                        }
                      }}
                      title={isCurrent ? `@${acc.username} (Active)` : `Switch to @${acc.username}`}
                    >
                      <img 
                        src={`https://steemitimages.com/u/${acc.username}/avatar`} 
                        alt={acc.username}
                        style={{ width: '22px', height: '22px', borderRadius: '50%', marginRight: '6px', objectFit: 'cover' }}
                        onError={(e) => { (e.target as HTMLImageElement).src = "https://steemitimages.com/u/steem/avatar"; }}
                      />
                      <span className="small font-weight-600" style={{ color: 'var(--letters)' }}>
                        @{acc.username}
                      </span>
                      {isCurrent ? (
                        <span className="badge badge-success ml-1" style={{ fontSize: '0.55rem', padding: '2px 4px' }}>Active</span>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-link text-muted p-0 ml-1"
                          style={{ border: 'none', background: 'none' }}
                          title="Remove"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeSavedAccount(acc.username);
                          }}
                        >
                          <i className="fas fa-times text-danger" style={{ fontSize: '0.65rem', opacity: 0.6 }} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="text-center mb-4">
            <img 
              src={avatarUrl} 
              alt="Avatar" 
              className="rounded-circle shadow" 
              style={{ width: "80px", height: "80px", objectFit: "cover", border: "3px solid #fff" }}
              onError={(e) => { (e.target as HTMLImageElement).src = "https://steemitimages.com/u/steem/avatar"; }}
            />
          </div>
          <div className="text-center text-muted mb-4">
            {requiredActive
              ? "Active key required to sign transaction."
              : "Sign in with Steem account."}
          </div>
            <Form role="form">
              <FormGroup className="mb-3">
                <div className={`input-icon-group ${focusUser ? "focused" : ""}`}>
                  <i className="fas fa-user input-icon" />
                  <input
                    id="username"
                    className="form-control fancy-input"
                    name="username"
                    placeholder="Username"
                    type="text"
                    disabled={requiredActive}
                    {...register("username")}
                    onFocus={() => setFocusUser(true)}
                    onBlur={() => setFocusUser(false)}
                  />
                </div>

                <div className={`input-icon-group mt-3 ${focusPass ? "focused" : ""}`}>
                  <i className="fas fa-key input-icon" />
                  <input
                    id="wif"
                    className="form-control fancy-input"
                    name="wif"
                    placeholder={requiredActive ? "Active Key" : "Posting Key"}
                    type="password"
                    {...register("wif")}
                    onFocus={() => setFocusPass(true)}
                    onBlur={() => setFocusPass(false)}
                  />
                </div>

                {!requiredActive && (
                  <div className="custom-control custom-checkbox mt-3 text-left">
                    <input
                      type="checkbox"
                      className="custom-control-input"
                      id="remindAccountCheckbox"
                      checked={remindAccount}
                      onChange={(e) => setRemindAccount(e.target.checked)}
                    />
                    <label className="custom-control-label small text-muted" htmlFor="remindAccountCheckbox" style={{ cursor: 'pointer' }}>
                      Remember this account
                    </label>
                  </div>
                )}

                <div className="mt-3">
                  <Label className="small mb-0 w-100 text-center">
                    Don't have an account? <u><a href="#" onClick={(e) => { e.preventDefault(); hide(); if(onSignup) onSignup(); }}>Create one</a></u>
                  </Label>
                </div>
              </FormGroup>
            <div className="text-center">
              <Button
                color="default"
                type="button"
                className="w-100"
                onClick={handleSubmit(onSubmit)} 
              >
                Sign in 
              </Button>
              <span className="form-info w-100">* Leave empty to use Steem Keychain</span> 

              <div className="mt-4 pt-3 border-top">
                <input 
                  type="file" 
                  accept=".txt" 
                  ref={fileRef} 
                  style={{ display: "none" }} 
                  onChange={handleFileRead} 
                />
                <Button 
                  color="info" 
                  outline 
                  size="sm" 
                  className="w-100 btn-icon" 
                  onClick={(e) => { e.preventDefault(); fileRef.current?.click(); }}
                  style={{ borderStyle: "dashed", opacity: 0.8 }}
                >
                  <i className="fas fa-file-alt mr-2" /> Login with keys file (.txt)
                </Button>
              </div>
            </div>
          </Form>
        </CardBody>
      </Card>
    </div>
  );

  return (
    isShowing ? ReactDOM.createPortal(
      <React.Fragment>
        <AppModal
          isShowing={isShowing}
          hide={hide}
          size="md"
          header="Login"
          children={renderForm}
        />
      </React.Fragment>, document.body
    ) : null)
};

export default LoginModal;